import type { FC } from "react";

import type { TimingEntry } from "../../types/timings";

import { formatDateTime, formatDurationMs } from "../../utils/time";
import { DataTable } from "../common/DataTable";
import { EntriesTableShell } from "../common/EntriesTableShell";

const EMPTY = "\u2014";

export interface TimingsEntriesTableProps {
  entries: TimingEntry[];
  total: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  errorMessage?: string | null;
  onPageChange: (page: number) => void;
}

export const TimingsEntriesTable: FC<TimingsEntriesTableProps> = ({
  entries,
  total,
  page,
  pageSize,
  isLoading,
  errorMessage,
  onPageChange,
}) => (
  <EntriesTableShell
    total={total}
    page={page}
    pageSize={pageSize}
    isLoading={isLoading && entries.length === 0}
    errorMessage={errorMessage}
    isEmpty={total === 0 && entries.length === 0 && !isLoading}
    emptyTitle="No timing entries"
    emptyDescription="Try expanding the date range or clearing filters."
    onPageChange={onPageChange}
  >
    <DataTable responsive className="text-xs">
      <DataTable.Header>
        <tr>
          <DataTable.HeaderCell>Started</DataTable.HeaderCell>
          <DataTable.HeaderCell>Duration</DataTable.HeaderCell>
          <DataTable.HeaderCell>Kind</DataTable.HeaderCell>
          <DataTable.HeaderCell>Name</DataTable.HeaderCell>
          <DataTable.HeaderCell>Task</DataTable.HeaderCell>
          <DataTable.HeaderCell>Run</DataTable.HeaderCell>
          <DataTable.HeaderCell>Step</DataTable.HeaderCell>
        </tr>
      </DataTable.Header>
      <DataTable.Body>
        {entries.map((entry) => (
          <DataTable.Row key={entry.id}>
            <DataTable.Cell className="whitespace-nowrap text-xs text-gray-500 dark:text-white/60">
              {formatDateTime(entry.started_at)}
            </DataTable.Cell>
            <DataTable.Cell className="text-right font-mono text-xs">
              {formatDurationMs(entry.duration_ms)}
            </DataTable.Cell>
            <DataTable.Cell className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-slate-900 dark:text-white">
              {entry.kind}
            </DataTable.Cell>
            <DataTable.Cell className="max-w-[180px] truncate text-xs text-slate-600 dark:text-slate-300">
              {entry.name ?? EMPTY}
            </DataTable.Cell>
            <DataTable.Cell className="max-w-[180px] truncate font-mono text-xs text-slate-600 dark:text-slate-300">
              {entry.task_id}
            </DataTable.Cell>
            <DataTable.Cell className="max-w-[180px] truncate font-mono text-xs text-slate-600 dark:text-slate-300">
              {entry.run_id ?? EMPTY}
            </DataTable.Cell>
            <DataTable.Cell className="text-xs text-slate-600 dark:text-slate-300">
              {entry.step_number ?? EMPTY}
            </DataTable.Cell>
          </DataTable.Row>
        ))}
      </DataTable.Body>
    </DataTable>
  </EntriesTableShell>
);
